import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpFromLine, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { productsApi, inventoryApi } from '@/api/client';
import type { Product, Inventory, StockOutForm } from '@/types';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function StockOutPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [fifoInventory, setFifoInventory] = useState<Inventory[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFifo, setIsLoadingFifo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [form, setForm] = useState<StockOutForm>({
    inventory_id: 0,
    quantity: 0,
    note: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      fetchFifoInventory(selectedProductId);
    } else {
      setFifoInventory([]);
    }
  }, [selectedProductId]);

  const fetchProducts = async () => {
    const response = await productsApi.list({ limit: 100 });
    if (response.success && response.data) {
      setProducts(response.data.items);
    }
    setIsLoading(false);
  };

  const fetchFifoInventory = async (productId: number) => {
    setIsLoadingFifo(true);
    const response = await inventoryApi.fifo(productId);
    if (response.success && response.data) {
      const data = response.data;
      setFifoInventory(data);
      // Auto-select first (FIFO recommended) lot
      if (data.length > 0) {
        setForm(f => ({ ...f, inventory_id: data[0].id }));
      }
    }
    setIsLoadingFifo(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // 整数チェック
    if (!Number.isInteger(form.quantity) || form.quantity <= 0) {
      setError('数量は1以上の整数を入力してください');
      setIsSubmitting(false);
      return;
    }

    const response = await inventoryApi.stockOut(form);

    if (response.success) {
      setSuccess(true);
      // Refresh FIFO list
      if (selectedProductId) {
        await fetchFifoInventory(selectedProductId);
      }
      setTimeout(() => {
        setSuccess(false);
        setForm(f => ({ ...f, quantity: 0, note: '' }));
      }, 2000);
    } else {
      setError(response.error || '出庫登録に失敗しました');
    }
    setIsSubmitting(false);
  };

  const selectedInventory = fifoInventory.find(i => i.id === form.inventory_id);
  const selectedProduct = products.find(p => p.id === selectedProductId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <ArrowUpFromLine className="h-6 w-6 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">出庫登録</h1>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">出庫を登録しました</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Product Selection */}
        <div>
          <label className="label">商品 *</label>
          <select
            value={selectedProductId || ''}
            onChange={(e) => {
              setSelectedProductId(parseInt(e.target.value) || 0);
              setForm({ inventory_id: 0, quantity: 0, note: '' });
            }}
            className="input"
          >
            <option value="">商品を選択してください</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} （在庫: {product.total_stock ?? 0}{product.unit}）
              </option>
            ))}
          </select>
        </div>

        {/* FIFO Inventory Selection */}
        {selectedProductId > 0 && (
          <div>
            <label className="label">ロット選択 *</label>
            <p className="text-sm text-gray-500 mb-2">
              🔄 FIFO順（期限が近い順）で表示しています
            </p>
            
            {isLoadingFifo ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : fifoInventory.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                在庫がありません
              </div>
            ) : (
              <div className="space-y-2">
                {fifoInventory.map((inv, index) => {
                  const daysUntilExpiry = inv.days_until_expiry ?? null;
                  return (
                    <label
                      key={inv.id}
                      className={clsx(
                        'block border rounded-lg p-3 cursor-pointer transition-colors',
                        form.inventory_id === inv.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="inventory_id"
                          value={inv.id}
                          checked={form.inventory_id === inv.id}
                          onChange={(e) => setForm({ ...form, inventory_id: parseInt(e.target.value) })}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{inv.lot_number}</span>
                            {index === 0 && (
                              <span className="badge-success text-xs">推奨</span>
                            )}
                            {daysUntilExpiry !== null && daysUntilExpiry <= 0 && (
                              <span className="badge-danger text-xs flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                期限切れ
                              </span>
                            )}
                            {daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 7 && (
                              <span className="badge-warning text-xs">
                                あと{daysUntilExpiry}日
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            在庫: {inv.quantity}{selectedProduct?.unit} / 
                            期限: {inv.expiry_date 
                              ? format(parseISO(inv.expiry_date), 'yyyy/MM/dd', { locale: ja })
                              : '未設定'}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Quantity */}
        {selectedInventory && (
          <div>
            <label className="label">出庫数量 *</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                required
                min="1"
                max={selectedInventory.quantity}
                step="1"
                value={form.quantity || ''}
                onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
                className="input"
                placeholder="0"
              />
              {selectedProduct && (
                <span className="text-gray-500 whitespace-nowrap">
                  {selectedProduct.unit}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              最大: {selectedInventory.quantity}{selectedProduct?.unit}（整数で入力）
            </p>
          </div>
        )}

        {/* Note */}
        <div>
          <label className="label">メモ</label>
          <textarea
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="input"
            rows={2}
            placeholder="出庫先、用途など（任意）"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !form.inventory_id || !form.quantity}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <ArrowUpFromLine className="h-4 w-4" />
                出庫登録
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
